/**
 * Stale-client detection for the served web app.
 *
 * Every web build writes `/version.json` (`{ sha, builtAt }`, served `no-cache`) and embeds the same
 * short commit as `BUILD_CONFIG.appCommit`. A tab whose running commit differs from the served one is
 * running an older (or newer) release than the server it talks to; it is offered a reload. A missing,
 * unreachable or malformed stamp means "we could not ask" — never "a new version": nothing happens.
 */

export const VERSION_STAMP_URL = '/version.json';
export const CHECK_INTERVAL_MS = 15 * 60_000;
/** localStorage: the served commit the user already dismissed (or reloaded for) — never re-offered. */
export const DISMISSED_KEY = 'pipe-app-update-dismissed';

export interface ServedVersion {
  sha: string;
  builtAt: string | null;
}

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem'>;

const SHA = /^[0-9a-f]{7,40}$/;

export function parseServedVersion(body: unknown): ServedVersion | null {
  if (typeof body !== 'object' || body === null) return null;
  const { sha, builtAt } = body as Record<string, unknown>;
  if (typeof sha !== 'string' || !SHA.test(sha)) return null;
  return { sha, builtAt: typeof builtAt === 'string' ? builtAt : null };
}

/** The served build stamp, or `null` when it cannot be read for any reason (never throws). */
export async function fetchServedVersion(
  fetchImpl: typeof fetch = fetch
): Promise<ServedVersion | null> {
  try {
    const res = await fetchImpl(VERSION_STAMP_URL, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    return parseServedVersion(await res.json());
  } catch {
    return null;
  }
}

function read(store: KeyValueStore | null, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(store: KeyValueStore | null, key: string, value: string) {
  try {
    store?.setItem(key, value);
  } catch {
    // Storage blocked (private mode): the in-memory guard still prevents a repeat this session.
  }
}

export interface AppVersionWatcherOptions {
  /** This bundle's commit (`BUILD_CONFIG.appCommit`); empty disables every check. */
  runningSha: string;
  fetchVersion: () => Promise<ServedVersion | null>;
  /** A served commit differs from the running one and has not been offered or dismissed. */
  onNewVersion: (sha: string) => void;
  /** The served commit equals the running one again (e.g. a release was rolled back). */
  onCurrent?: () => void;
  storage?: KeyValueStore | null;
}

export class AppVersionWatcher {
  private offered: string | null = null;
  private inflight: Promise<void> | null = null;
  private dismissedInSession: string | null = null;

  constructor(private readonly opts: AppVersionWatcherOptions) {}

  /** One check; concurrent callers share the request in flight. */
  check(): Promise<void> {
    if (!this.opts.runningSha) return Promise.resolve();
    this.inflight ??= this.run().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  /** The user closed the prompt (or reloaded from it): never offer this commit again. */
  dismiss(sha: string) {
    this.dismissedInSession = sha;
    write(this.opts.storage ?? null, DISMISSED_KEY, sha);
  }

  /**
   * Check now, whenever the tab becomes visible or focused again, and every
   * {@link CHECK_INTERVAL_MS}. Returns the stop function.
   */
  start(win: Window, doc: Document): () => void {
    const check = () => void this.check();
    const onVisible = () => {
      if (doc.visibilityState === 'visible') check();
    };
    win.addEventListener('focus', check);
    doc.addEventListener('visibilitychange', onVisible);
    const timer = win.setInterval(check, CHECK_INTERVAL_MS);
    check();
    return () => {
      win.removeEventListener('focus', check);
      doc.removeEventListener('visibilitychange', onVisible);
      win.clearInterval(timer);
    };
  }

  private async run() {
    const served = await this.opts.fetchVersion();
    if (!served) return;
    if (served.sha === this.opts.runningSha) {
      if (this.offered !== null) {
        this.offered = null;
        this.opts.onCurrent?.();
      }
      return;
    }
    if (
      served.sha === this.offered ||
      served.sha === this.dismissedInSession ||
      served.sha === read(this.opts.storage ?? null, DISMISSED_KEY)
    )
      return;
    this.offered = served.sha;
    this.opts.onNewVersion(served.sha);
  }
}
